import { Component, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { COMMON_IMPORTS, MATERIAL_IMPORTS } from '../../imports/common-imports';
import { LambdaApiService } from '../../services/lambda-api.service';
import { FilterStateService } from '../../services/filter-state.service';
import { Region } from '../../models/region.model';
import { SpinnerComponent } from '../spinner/spinner.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Market } from '../../models/market.model';

export interface CompareDialogData {
  ta: string;
  element: string;
}

@Component({
  selector: 'app-compare-dialog',
  standalone: true,
  imports: [COMMON_IMPORTS, MATERIAL_IMPORTS, SpinnerComponent],
  templateUrl: './compare-dialog.component.html',
  styleUrl: './compare-dialog.component.scss',
})
export class CompareDialogComponent {
  typeSelected: string = '';
  regions: { id: string; name: string; isSelected: boolean }[] = [];
  markets: (Market & { checked: boolean })[] = [];
  isLoading: boolean = false;
  constructor(
    private readonly apiService: LambdaApiService,
    private readonly filterStateService: FilterStateService,
    private readonly dialogRef: MatDialogRef<CompareDialogComponent>,
    private readonly router: Router,
    @Inject(MAT_DIALOG_DATA) private readonly data: CompareDialogData,
  ) {}

  ngOnInit() {
    this.loadRegions();
  }

  loadRegions() {
    this.isLoading = true;
    const userAvailableRegions =
      this.filterStateService.pristineFilters.regions;
    this.apiService
      .getRegions()
      .then((data: Region[]) => {
        data = data.filter((region) =>
          userAvailableRegions.includes(region.id),
        );
        this.regions = data.map((region: any) => ({
          ...region,
          isSelected: false,
        }));
        this.loadMarkets();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  loadMarkets() {
    this.isLoading = true;
    const availableMarkets = new Set(
      this.filterStateService.pristineFilters.markets,
    );
    this.apiService
      .getMarkets()
      .then((data: Market[]) => {
        // Filter markets if pristineFilters.markets is not _ALL
        if (!availableMarkets.has('_ALL')) {
          data = data.filter((market) => availableMarkets.has(market.name));
        }

        this.markets = data.map((market) => ({ ...market, checked: false }));

        // order markets by name
        this.markets.sort((a, b) => a.name.localeCompare(b.name));
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  applySelection() {
    //upon save, clear region and market filters
    this.filterStateService.filterData.markets = [
      ...this.filterStateService.pristineFilters.markets,
    ];
    this.filterStateService.filterData.regions = [
      ...this.filterStateService.pristineFilters.regions,
    ];
    // Clear applied filter keys when resetting to pristine
    this.filterStateService.clearFilter('regions');
    this.filterStateService.clearFilter('markets');

    const selectedData =
      this.typeSelected === 'region'
        ? this.getSelectedRegions().map((region) => region.id)
        : this.markets
            .filter((market) => market.checked)
            .map((market) => market.name);

    this.router.navigate([
      '/details',
      this.data.ta,
      this.data.element,
      'compare',
      this.typeSelected,
      selectedData[0],
      selectedData[1],
    ]);

    this.dialogRef.close();
  }

  getSelectedRegions(): { id: string; name: string; isSelected: boolean }[] {
    return this.regions.filter((region) => region.isSelected);
  }

  isApplyDisabled(): boolean {
    if (this.typeSelected === 'region') {
      return this.getSelectedRegions().length !== 2;
    }
    return this.getSelectedMarkets().length !== 2;
  }

  getSelectedMarkets(): (Market & { checked: boolean })[] {
    return this.markets.filter((market) => market.checked);
  }
}
